import { useState } from 'react'
import { PermissionButton } from '../components/Permissions.js'
import { useParams } from 'react-router-dom'
import { useApi, useAsync } from '../lib/useApi.js'
import { ApiError } from '../lib/api.js'
import { CopyButton, Dialog, PageHeader, Empty, ErrorBox, Loading } from '../components/ui.js'
import {
  NO_ORIGINS, ORIGINS_UNRECORDED, checkOrigins, originsView, parseOrigins,
} from '../lib/auth-setup.js'
import {
  DISABLE_CONSEQUENCES, PROVIDERS, REVOKE_CONSEQUENCES, byId, checkCredentials,
  recoveryNote, savedMessage, type ProviderId,
} from '../lib/auth-providers.js'
import { BEFORE_AUTH } from '../lib/boundaries.js'
import { UserDirectory } from '../components/UserDirectory.js'
import { EmailPreview } from '../components/EmailPreview.js'

/**
 * Sign-up and sign-in for the CUSTOMER'S OWN users.
 *
 * THE CONFUSION THIS PAGE HAS TO DEFEAT ON SIGHT
 *
 * There are two unrelated things called auth here. Signing in to this
 * dashboard is Clerk and is ours. This page is the other one: a customer's
 * app having its own users, who are rows in the customer's own database and
 * have never heard of NimblyBase. Somebody who conflates them will look for
 * their app's users in the Members page and find their colleagues.
 *
 * So the page says whose users these are in its first sentence, and says it
 * again when switching the feature off - because "turn off auth" is a
 * sentence that could plausibly mean "lock me out", and it does not.
 *
 * WHY A PAGE RATHER THAN A SWITCH ON THE PROJECT
 *
 * Because of trustedOrigins. It is a CSRF control, the only security
 * decision a customer makes when turning this on, and a control like that
 * on a page of other settings gets left at its default by everyone. Here it
 * has room to say what it is for.
 */
export default function Auth() {
  const { id = '' } = useParams()
  const api = useApi()

  const project = useAsync(() => api.getProject(id), [api, id])
  const [reloadKey, setReloadKey] = useState(0)
  const status = useAsync(() => api.projectAuth(id), [api, id, reloadKey])

  const [origins, setOrigins] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [confirmDisable, setConfirmDisable] = useState(false)
  const [confirmRevoke, setConfirmRevoke] = useState(false)
  const [notice, setNotice] = useState<{ tone: 'ok' | 'warn'; text: string } | null>(null)
  const [editing, setEditing] = useState<ProviderId | null>(null)
  /** The origins box on an already-enabled project, or null when not editing. */
  const [editOrigins, setEditOrigins] = useState<string | null>(null)
  const [clientId, setClientId] = useState('')
  const [clientSecret, setClientSecret] = useState('')

  const p = project.data?.project
  const parsed = parseOrigins(origins)
  const problems = checkOrigins(parsed)

  async function enable(e: React.FormEvent) {
    e.preventDefault()
    if (busy || problems.length > 0) return
    setBusy(true)
    setError(null)
    try {
      await api.enableAuth(id, parsed)
      setReloadKey((k) => k + 1)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not turn auth on.')
    } finally {
      setBusy(false)
    }
  }

  /**
   * Re-post the origins to the same endpoint that first set them.
   *
   * Enabling is idempotent by design - a provider change already re-enters
   * it - so this is a republish with a new allowlist rather than a second
   * kind of write, and there is no second code path to keep in step.
   */
  async function saveOrigins(e: React.FormEvent, next: string[]) {
    e.preventDefault()
    if (busy) return
    setBusy(true)
    setError(null)
    setNotice(null)
    try {
      await api.enableAuth(id, next)
      setEditOrigins(null)
      setReloadKey((k) => k + 1)
      setNotice({ tone: 'ok', text: 'Trusted origins updated. The auth service was republished.' })
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not update the trusted origins.')
    } finally {
      setBusy(false)
    }
  }

  async function saveProvider(e: React.FormEvent, provider: ProviderId, label: string) {
    e.preventDefault()
    if (busy || checkCredentials({ clientId, clientSecret }).length > 0) return
    setBusy(true)
    setError(null)
    try {
      const result = await api.setAuthProvider(id, provider, { clientId, clientSecret })
      // Never "saved": `live` false means the running auth service still
      // has the old credentials, and the customer's next act is to test a
      // sign-in that will use them.
      setNotice(savedMessage(result, label))
      setEditing(null)
      setClientId('')
      setClientSecret('')
      setReloadKey((k) => k + 1)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : `Could not configure ${label}.`)
    } finally {
      setBusy(false)
    }
  }

  async function removeProvider(provider: ProviderId, label: string) {
    if (busy) return
    setBusy(true)
    setError(null)
    try {
      const result = await api.deleteAuthProvider(id, provider)
      setNotice({ tone: result.live ? 'ok' : 'warn', text: result.message })
      setReloadKey((k) => k + 1)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : `Could not remove ${label}.`)
    } finally {
      setBusy(false)
    }
  }

  async function revoke() {
    if (busy) return
    setBusy(true)
    setError(null)
    try {
      const result = await api.revokeAuthSessions(id)
      setNotice({ tone: 'ok', text: `${result.sessions} session(s) ended. ${result.message}` })
      setConfirmRevoke(false)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Sessions could not be ended.')
    } finally {
      setBusy(false)
    }
  }

  async function disable() {
    if (busy) return
    setBusy(true)
    setError(null)
    try {
      await api.disableAuth(id)
      setConfirmDisable(false)
      setReloadKey((k) => k + 1)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not turn auth off.')
    } finally {
      setBusy(false)
    }
  }

  if (project.loading) return <Loading />
  if (project.error) return <ErrorBox>{project.error}</ErrorBox>
  if (!p) return <Empty>Project not found.</Empty>

  const s = status.data

  return (
    <>
      <PageHeader
        title="Authentication"
        description="Configure sign-up, sign-in, and sessions for your application."
      />

      {/* Said before anything else, because the whole page is misread
          otherwise. */}
      <div className="info-banner" style={{ marginBottom: 16 }}>
        <strong>These are the people who use the app you are building.</strong>
        <p className="sub small" style={{ margin: '6px 0 0' }}>
          Not your team &mdash; they are on the Members page. Your app&rsquo;s users are rows in your own
          database, in reserved auth tables. These tables are hidden from the row API to protect passwords,
          sessions and signing keys.
        </p>
        {/* The consequence, which the paragraph above does not say: whose
            apps these users can then sign in to. */}
        <p className="sub small" style={{ margin: '6px 0 0' }}>
          {BEFORE_AUTH}
        </p>
      </div>

      {error && <ErrorBox>{error}</ErrorBox>}
      {notice && (
        <div className={`notice ${notice.tone === 'warn' ? 'notice-danger' : ''}`} style={{ marginBottom: 12 }}>
          <p className="sub small" style={{ margin: 0 }}>{notice.text}</p>
        </div>
      )}

      {status.loading ? (
        <Loading />
      ) : status.error ? (
        <ErrorBox retry={status.reload}>{status.error}</ErrorBox>
      ) : !s ? (
        <Empty>Could not read the auth status.</Empty>
      ) : s.enabled ? (
        <>
          <div className="card" style={{ padding: 16, marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
              <span className="pill pill-active">on</span>
              {s.providers.map((prov) => (
                <span className="pill" key={prov}>
                  {prov}
                </span>
              ))}
            </div>

            <p className="sub small" style={{ margin: '12px 0 4px' }}>
              Auth API base URL for sign-up, sign-in and sessions. Opening the base URL itself
              returns 404 because it is an API prefix, not a web page.
            </p>
            <div className="endpoint-line">
              <code>{s.url}</code>
              <CopyButton value={s.url} label="Copy auth URL" />
            </div>
            <p className="sub small" style={{ marginTop: 8 }}>
              Runtime check: <code>{s.url}/_health</code>
            </p>
            <h2>Trusted browser origins</h2>
            {editOrigins === null ? (
              <>
                {(() => {
                  const view = originsView(s.trustedOrigins)
                  if (view.kind === 'unrecorded') {
                    return <p className="sub small">{ORIGINS_UNRECORDED}</p>
                  }
                  if (view.kind === 'none') {
                    return (
                      <p className="sub small">
                        No origin is trusted, so no browser can sign your users in. {NO_ORIGINS}
                      </p>
                    )
                  }
                  return (
                    <ul className="sub small mono" style={{ margin: '4px 0 0 18px' }}>
                      {view.origins.map((o) => <li key={o}>{o}</li>)}
                    </ul>
                  )
                })()}
                <button
                  className="btn btn-small"
                  style={{ marginTop: 10 }}
                  onClick={() => setEditOrigins((s.trustedOrigins ?? []).join('\n'))}
                >
                  {s.trustedOrigins == null ? 'Set origins' : 'Edit origins'}
                </button>
              </>
            ) : (
              <form onSubmit={(e) => void saveOrigins(e, parseOrigins(editOrigins))}>
                <div className="field">
                  <label htmlFor="edit-origins">Trusted browser origins</label>
                  <textarea
                    id="edit-origins"
                    rows={3}
                    value={editOrigins}
                    placeholder={'https://app.example.com\nhttp://localhost:5173'}
                    autoCapitalize="off"
                    autoCorrect="off"
                    spellCheck={false}
                    onChange={(e) => setEditOrigins(e.target.value)}
                    style={{ width: '100%', fontFamily: 'var(--mono)', fontSize: 12.5 }}
                  />
                  <p className="sub small" style={{ margin: '6px 0 0' }}>
                    One per line. Saving republishes the auth service; nobody is signed out.
                  </p>
                  {checkOrigins(parseOrigins(editOrigins)).map((problem) => (
                    <p key={problem.value} className="sub small" style={{ margin: '6px 0 0' }}>
                      <span className="mono">{problem.value}</span> {problem.why}
                    </p>
                  ))}
                </div>
                <button
                  className="btn btn-primary btn-small"
                  type="submit"
                  disabled={busy || checkOrigins(parseOrigins(editOrigins)).length > 0}
                >
                  {busy ? 'Saving\u2026' : 'Save origins'}
                </button>{' '}
                <button className="btn btn-small" type="button" onClick={() => setEditOrigins(null)}>
                  Cancel
                </button>
              </form>
            )}

            <EmailPreview projectId={id} />

            <p className="sub small" style={{ margin: '12px 0 0' }}>
              After signing in, call <span className="mono">GET /auth/token</span> with the session cookie to
              obtain a data JWT. Send that JWT in the <span className="mono">x-user-token</span> header
              alongside your project&rsquo;s anon key. The session token returned by sign-up or sign-in is not
              a data JWT. Row policies then see them as <span className="mono">authenticated</span>, and{' '}
              <span className="mono">ctx.userId</span> is their id.
            </p>
            <p className="sub small" style={{ margin: '8px 0 0' }}>
              Data JWTs last 15 minutes, plus clock-skew allowance. Signing out prevents new tokens but does
              not revoke an already issued JWT. Ending every session stops them too, within five minutes
              &mdash; the edge caches the signing keys for that long.
            </p>
          </div>

          {recoveryNote(s.recovery) && (
            <div className="notice notice-danger" style={{ marginBottom: 16 }}>
              <p className="sub small" style={{ margin: 0 }}>{recoveryNote(s.recovery)}</p>
            </div>
          )}

          <div className="card" style={{ padding: 16, marginBottom: 16 }}>
            <h2 style={{ marginTop: 0, fontSize: 15 }}>Sign in with Google or GitHub</h2>
            <p className="sub small" style={{ margin: '0 0 12px' }}>
              Create an OAuth app in the provider&rsquo;s console, then paste its client id and secret here.
              The callback URL below has to be registered there exactly &mdash; when it does not match, the
              provider shows an error that names neither URL.
            </p>

            {PROVIDERS.map((prov) => {
              const configured = byId(s.social).get(prov.id)
              const open = editing === prov.id
              const problems = open ? checkCredentials({ clientId, clientSecret }) : []
              return (
                <div key={prov.id} style={{ borderTop: '1px solid var(--line)', padding: '12px 0' }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
                    <strong>{prov.label}</strong>
                    {configured
                      ? <span className="pill pill-active">configured</span>
                      : <span className="pill">off</span>}
                    {configured && (
                      <span className="sub small mono">{configured.clientId}</span>
                    )}
                  </div>

                  {configured && (
                    <>
                      <p className="sub small" style={{ margin: '10px 0 4px' }}>
                        Register this as the {prov.field} in{' '}
                        <a href={prov.console} target="_blank" rel="noreferrer">the console</a>:
                      </p>
                      <div className="endpoint-line">
                        <code>{configured.callbackUrl}</code>
                        <CopyButton value={configured.callbackUrl} label="Copy callback URL" />
                      </div>
                    </>
                  )}

                  {open ? (
                    <form onSubmit={(e) => saveProvider(e, prov.id, prov.label)} style={{ marginTop: 12 }}>
                      <div className="field">
                        <label htmlFor={`${prov.id}-id`}>Client id</label>
                        <input
                          id={`${prov.id}-id`} value={clientId} autoComplete="off" spellCheck={false}
                          onChange={(e) => setClientId(e.target.value)}
                          style={{ width: '100%', fontFamily: 'var(--mono)', fontSize: 12.5 }}
                        />
                      </div>
                      <div className="field">
                        <label htmlFor={`${prov.id}-secret`}>Client secret</label>
                        <input
                          id={`${prov.id}-secret`} type="password" value={clientSecret}
                          autoComplete="off" spellCheck={false}
                          onChange={(e) => setClientSecret(e.target.value)}
                          style={{ width: '100%', fontFamily: 'var(--mono)', fontSize: 12.5 }}
                        />
                        {/* It cannot be shown back, so an empty box does not
                            mean "not configured" - it means "type a new one". */}
                        <p className="sub small" style={{ margin: '6px 0 0' }}>
                          Stored encrypted and never shown again. Saving replaces the current secret.
                        </p>
                      </div>
                      {problems.length > 0 && (
                        <div className="notice notice-danger" style={{ marginBottom: 12 }}>
                          {problems.map((prob) => (
                            <p className="sub small" style={{ margin: 0 }} key={prob.field}>{prob.why}</p>
                          ))}
                        </div>
                      )}
                      <PermissionButton
                        permission="project:update" className="btn btn-primary"
                        disabled={busy || problems.length > 0}
                      >
                        {busy ? 'Saving…' : `Save ${prov.label}`}
                      </PermissionButton>
                      <button
                        type="button" className="btn" style={{ marginLeft: 8 }}
                        onClick={() => { setEditing(null); setClientId(''); setClientSecret('') }}
                      >
                        Cancel
                      </button>
                    </form>
                  ) : (
                    <div style={{ marginTop: 10 }}>
                      <PermissionButton
                        permission="project:update" className="btn"
                        onClick={() => {
                          setNotice(null)
                          setEditing(prov.id)
                          setClientId(configured?.clientId ?? '')
                          setClientSecret('')
                        }}
                      >
                        {configured ? 'Replace credentials' : `Configure ${prov.label}`}
                      </PermissionButton>
                      {configured && (
                        <PermissionButton
                          permission="project:update" className="btn btn-danger" style={{ marginLeft: 8 }}
                          disabled={busy}
                          onClick={() => removeProvider(prov.id, prov.label)}
                        >
                          Remove
                        </PermissionButton>
                      )}
                    </div>
                  )}
                </div>
              )
            })}
          </div>

          <div className="card" style={{ padding: 16, marginBottom: 16 }}>
            <h2 style={{ marginTop: 0, fontSize: 15 }}>End every session</h2>
            <p className="sub small" style={{ margin: '0 0 12px' }}>
              For a leaked password or a compromised session. This is not what disabling auth does.
            </p>
            <ul className="sub small" style={{ margin: '0 0 12px 18px' }}>
              {REVOKE_CONSEQUENCES.map((line) => <li key={line}>{line}</li>)}
            </ul>
            <PermissionButton
              permission="project:delete" className="btn btn-danger" disabled={busy}
              onClick={() => { setError(null); setNotice(null); setConfirmRevoke(true) }}
            >
              {busy ? 'Working…' : 'Sign everyone out'}
            </PermissionButton>
          </div>

          <div className="card" style={{ padding: 16 }}>
            <h2 style={{ marginTop: 0, fontSize: 15 }}>Disable authentication</h2>
            <ul className="sub small" style={{ margin: '0 0 12px 18px' }}>
              {DISABLE_CONSEQUENCES.map((line) => <li key={line}>{line}</li>)}
            </ul>
            <PermissionButton
              permission="project:update"
              className="btn btn-danger"
              disabled={busy}
              onClick={() => {
                setError(null)
                setConfirmDisable(true)
              }}
            >
              {busy ? 'Working…' : 'Stop serving auth'}
            </PermissionButton>
          </div>
        </>
      ) : (
        <form className="card" onSubmit={enable} style={{ padding: 16 }}>
          <p className="sub" style={{ margin: '0 0 14px' }}>
            Off. Your users cannot sign up yet. Turning this on installs reserved auth tables in this
            project&rsquo;s database and starts serving sign-up, sign-in and sessions with email and password.
          </p>

          <div className="field">
            <label htmlFor="origins">Trusted browser origins</label>
            <textarea
              id="origins"
              rows={3}
              value={origins}
              placeholder={'https://app.example.com\nhttp://localhost:5173'}
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              onChange={(e) => setOrigins(e.target.value)}
              style={{ width: '100%', fontFamily: 'var(--mono)', fontSize: 12.5 }}
            />
            <p className="sub small" style={{ margin: '6px 0 0' }}>
              One per line. These are the sites allowed to sign your users in from a browser &mdash; it is a
              CSRF control, so keep it to origins you actually run.
            </p>
            {parsed.length === 0 && (
              <p className="sub small" style={{ margin: '6px 0 0' }}>
                {NO_ORIGINS}
              </p>
            )}
          </div>

          {problems.length > 0 && (
            <div className="notice notice-danger" style={{ marginBottom: 12 }}>
              {problems.map((prob) => (
                <p className="sub small" style={{ margin: 0 }} key={prob.value}>
                  <span className="mono">{prob.value}</span> &mdash; {prob.why}
                </p>
              ))}
            </div>
          )}

          <PermissionButton
            permission="project:update"
            className="btn btn-primary"
            disabled={busy || problems.length > 0}
          >
            {busy ? 'Turning on…' : 'Turn on auth'}
          </PermissionButton>
        </form>
      )}
      <UserDirectory projectId={id} enabled={Boolean(s?.enabled)} />
      <Dialog
        open={confirmRevoke}
        title="Sign every user out?"
        busy={busy}
        onClose={() => setConfirmRevoke(false)}
      >
        <ul className="sub" style={{ margin: '0 0 12px 18px' }}>
          {REVOKE_CONSEQUENCES.map((line) => <li key={line}>{line}</li>)}
        </ul>
        {error && <ErrorBox>{error}</ErrorBox>}
        <button className="btn btn-danger" disabled={busy} onClick={revoke}>
          {busy ? 'Ending…' : 'End every session'}
        </button>
      </Dialog>
      <Dialog
        open={confirmDisable}
        title="Disable application authentication?"
        busy={busy}
        onClose={() => setConfirmDisable(false)}
      >
        <p className="sub">
          Sign-up and sign-in will stop. Your dashboard login is unaffected.
        </p>
        {/* The part people get wrong, and the reason "I disabled auth after
            a breach" is not the same as signing everybody out. */}
        <ul className="sub" style={{ margin: '0 0 12px 18px' }}>
          {DISABLE_CONSEQUENCES.map((line) => <li key={line}>{line}</li>)}
        </ul>
        {error && <ErrorBox>{error}</ErrorBox>}
        <button className="btn btn-danger" disabled={busy} onClick={disable}>
          {busy ? 'Disabling…' : 'Disable authentication'}
        </button>
      </Dialog>
    </>
  )
}
